import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import { MemoryRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChakraProvider } from '@chakra-ui/react';
import theme from '@theme/index.ts';
import { UserProvider } from '@providers/useUserContext.tsx';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Fonts } from 'theme/fonts/index.tsx';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { HashRouter } from 'react-router-dom';
import './i18n.ts';
import './index.css';

import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: 'https://ea4ad12ecfc2f83b3b2017b1eabe5424@sentry.monday-sportsclub.com/3',
  integrations: [Sentry.captureConsoleIntegration(), Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['dynamo-cycling.com', 'punch-boxing.com', 'riise-studios.com'],

  // autoSessionTracking: true,
  // Configuration des replays
  replaysSessionSampleRate: 0.0, // Désactiver la capture automatique des sessions
  replaysOnErrorSampleRate: 1.0,

  // Autres configurations
  beforeSend(event) {
    // Ignorer les messages spécifiques de Facebook/Instagram
    if (
      event.message &&
      (event.message.includes('text_only:') ||
        event.message.includes('dom_only:') ||
        event.message.includes('~ Object:') ||
        event.message.includes('~ Stack Trace') ||
        event.message.includes('Error: The domain') ||
        event.message.includes('Cannot get CSS styles from') ||
        event.message.includes('is deprecated in StrictMode') ||
        event.message.includes('WARNING: Cookiebot script is included twice') ||
        event.message.includes('text_and_dom:') ||
        event.message.includes('FBNavDomContentLoaded:') ||
        event.message.includes('FBNavLoadEventEnd:') ||
        event.message.includes('FBNavLargestContentfulPaint:') ||
        event.message.includes('FBNavResponseEnd:') ||
        event.message.includes('INFO: createObjectURL url') ||
        event.message.includes('FBNavResponseStart:') ||
        event.message.includes('FBNavAmpDetect:') ||
        event.message.includes('FBNavINP:') ||
        event.message.includes('NavigationPerformanceLoggerAndroid') ||
        event.message.includes('Google Tag Manager') ||
        event.message.includes('chrome-extension:') ||
        event.message.includes('googletagmanager.com') ||
        event.message.includes('FBNavFirstContentfulPaint:') ||
        event.message.includes('[Meta Pixel] - Duplicate Pixel ID') ||
        event.message.includes('antifingerprint not defined yet') ||
        event.message.includes('Invalid call to runtime.sendMessage') ||
        event.message.includes('TSS:') ||
        event.message.includes('tss.ts') ||
        event.message.includes('ep.link_id:') ||
        event.message.includes('en: click') ||
        event.message.includes('en: scroll') ||
        event.message.includes('epn.percent_scrolled:') ||
        event.message.includes('sr:') ||
        event.message.includes('Event would be batched, but batching is disabled in debug mode') ||
        event.message.includes('_p:') ||
        event.message.includes('gdid:') ||
        event.message.includes('are:') ||
        event.message.includes('gcs:') ||
        event.message.includes('v:') ||
        event.message.includes('_eu:') ||
        event.message.includes('tid:') ||
        event.message.includes('sid:') ||
        event.message.includes('en:') ||
        event.message.includes('_et:') ||
        event.message.includes('gtm:') ||
        event.message.includes('_dbg:') ||
        event.message.includes('frm:') ||
        event.message.includes('ul:') ||
        event.message.includes('gcd:') ||
        event.message.includes('uam:') ||
        event.message.includes('npa:') ||
        event.message.includes('ep.anonymize_ip:') ||
        event.message.includes('uaw:') ||
        event.message.includes('uap:') ||
        event.message.includes('uapv:') ||
        event.message.includes('uamb:') ||
        event.message.includes('uafvl:') ||
        event.message.includes('uab:') ||
        event.message.includes('pscdl:') ||
        event.message.includes('CSS properties changed during touchmove') ||
        event.message.includes('[vite]') ||
        event.message.includes('dma_cps:') ||
        event.message.includes('dma:') ||
        event.message.includes('uaa:') ||
        event.message.includes('cid:') ||
        event.message.includes('tag_exp:') ||
        event.message.includes('gtm.load') ||
        event.message.includes('monday-mindbody/apps/client/dist/assets') ||
        event.message.includes('Currently in memory:') ||
        event.message.includes('Stack Trace:'))
    ) {
      return null;
    }

    // Garder le filtre existant pour les erreurs CSS et NotFoundError
    if (event.exception && event.exception.values) {
      const errorMessage = event.exception.values[0].value;
      const errorName = event.exception.values[0].type;
      if (
        (errorMessage && errorMessage.includes('Unable to preload CSS')) ||
        (errorName === 'NotFoundError' &&
          (errorMessage?.includes('The object can not be found here') ||
            errorMessage?.includes("Failed to execute 'removeChild' on 'Node'"))) ||
        (errorMessage && errorMessage.includes('cybotCookiebotDialogBodyButtonAccept'))
      ) {
        return null;
      }
    }
    return event;
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  },
});

persistQueryClient({
  queryClient,
  persister: createSyncStoragePersister({ storage: window.localStorage }),
});

const element = document.getElementById('mindbody-app');
if (element) {
  const root = ReactDOM.createRoot(element);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
          <Fonts />
          <UserProvider>
            <HashRouter>
              <App />
            </HashRouter>
          </UserProvider>
        </ChakraProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

class MondayMindbodyApp extends HTMLElement {
  connectedCallback() {
    const mountPoint = document.createElement('body');
    this.attachShadow({ mode: 'open' }).appendChild(mountPoint);

    const cache = createCache({
      key: 'monday-mindbody',
      container: mountPoint,
    });

    const root = ReactDOM.createRoot(mountPoint);

    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <CacheProvider value={cache}>
            <ChakraProvider theme={theme}>
              <Fonts />
              <UserProvider>
                <MemoryRouter>
                  <App />
                </MemoryRouter>
              </UserProvider>
            </ChakraProvider>
          </CacheProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}

customElements.define('monday-mindbody-app', MondayMindbodyApp);
